import { BreakpointObserver } from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
  viewChildren,
  ViewContainerRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { L10N_LOCALE, L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { map } from 'rxjs';
import { DashboardCustomizePanelComponent } from '../customize/dashboard-customize-panel.component';
import { DashboardCustomizeSession } from '../customize/dashboard-customize-session';
import { DashboardContext } from '../state/dashboard-context';
import { filterValueLabel } from '../state/dashboard-state';
import { DASHBOARD_CONFIG } from '../dashboard-grid.tokens';
import { DashboardWidgetRegistry, dashboardWidgetRegistryFactory } from '../widget/dashboard-widget-registry';
import { DashboardWidgetSettingsDialogComponent } from '../widget/dashboard-widget-settings-dialog.component';
import { DashboardWidgetFrameComponent } from '../frame/dashboard-widget-frame.component';
import { dashboardLayoutsFactory, DashboardLayouts } from '../layout/dashboard-layouts';
import {
  DashboardRefreshSettings,
  DashboardWidgetInstance,
  DEFAULT_REFRESH_INTERVAL_SECONDS,
} from '../layout/dashboard-layout';
import { DashboardWidgetDefinition, DashboardWidgetSize } from '../widget/dashboard-widget-definition';
import { DashboardViews } from '../views/dashboard-views';
import {
  DashboardGridArrangement,
  DashboardGridCanvasComponent,
  DashboardGridItemDirective,
} from './dashboard-grid-canvas.component';
import { describeInterval } from './describe-interval';
import { editingMinWidthPx } from './editing-min-width';

/**
 * Renders a configured dashboard: page toolbar, filter bar and the widget grid, with personal
 * Customize mode. Configure it with `provideDashboard(...)` on the page that renders it.
 */
@Component({
  selector: 'app-dashboard-grid',
  templateUrl: './dashboard-grid.component.html',
  styleUrls: ['./dashboard-grid.component.scss'],
  imports: [
    DatePipe,
    L10nTranslatePipe,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    DashboardCustomizePanelComponent,
    DashboardGridCanvasComponent,
    DashboardGridItemDirective,
    DashboardWidgetFrameComponent,
  ],
  providers: [
    DashboardContext,
    { provide: DashboardWidgetRegistry, useFactory: dashboardWidgetRegistryFactory },
    { provide: DashboardLayouts, useFactory: dashboardLayoutsFactory },
    DashboardViews,
    DashboardCustomizeSession,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardGridComponent implements OnInit {
  private readonly registry = inject(DashboardWidgetRegistry);
  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly config = inject(DASHBOARD_CONFIG);
  private readonly views = inject(DashboardViews);
  protected readonly session = inject(DashboardCustomizeSession);
  protected readonly context = inject(DashboardContext);
  private readonly translation = inject(L10nTranslationService);
  protected readonly locale = inject(L10N_LOCALE);

  protected readonly grid = this.config.grid;

  protected readonly isMobile = toSignal(
    this.breakpoints
      .observe(`(max-width: ${this.grid.mobileBreakpointPx - 0.02}px)`)
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );
  /** Null until measured */
  private readonly gridWidth = signal<number | null>(null);
  /** Customize never stacks, so the layout being edited is the one that is saved */
  protected readonly stacked = computed(() => {
    const width = this.gridWidth();
    return this.isMobile() || (!this.session.editing() && width !== null && width < this.grid.mobileBreakpointPx);
  });
  protected readonly editingMinWidth = computed(() =>
    this.session.editing() && !this.stacked() ? editingMinWidthPx(this.session.visibleWidgets(), this.grid) : null,
  );

  private readonly gridArea = viewChild.required<string, ElementRef<HTMLElement>>('gridArea', { read: ElementRef });

  protected readonly filterFields = computed(() => this.context.filterFields(this.session.layout()?.filters ?? []));
  protected readonly filterSummary = computed(() =>
    this.filterFields()
      .map((field) => filterValueLabel(field, this.context.valueOf(field.name)()))
      .filter((label): label is string => label !== undefined)
      .map((label) => this.translation.translate(label) as string),
  );
  protected readonly refresh = computed(() => this.session.layout()?.refresh, { equal: sameRefreshSettings });
  protected readonly refreshLabel = computed(() =>
    describeInterval(this.refresh()?.intervalSeconds ?? DEFAULT_REFRESH_INTERVAL_SECONDS, this.translation),
  );

  protected readonly sizeLimits = (type: string): DashboardWidgetSize | undefined => this.registry.sizeLimits(type);

  private readonly canvas = viewChild(DashboardGridCanvasComponent);
  private readonly panel = viewChild(DashboardCustomizePanelComponent);
  private readonly frames = viewChildren(DashboardWidgetFrameComponent);
  private readonly customizeButton = viewChild<string, ElementRef<HTMLButtonElement>>('customizeButton', {
    read: ElementRef,
  });

  constructor() {
    effect(() => {
      if (this.isMobile() && this.session.editing()) {
        this.session.cancel();
      }
    });

    // Saving, deleting or cancelling closes the panel; put focus back where Customize mode began
    let wasEditing = false;
    effect(() => {
      const editing = this.session.editing();
      if (wasEditing && !editing) {
        afterNextRender(() => this.customizeButton()?.nativeElement.focus(), { injector: this.injector });
      }

      wasEditing = editing;
    });

    this.context.autoRefresh(this.refresh);

    afterNextRender(() => {
      const area = this.gridArea().nativeElement;
      this.gridWidth.set(area.clientWidth);

      const resizes = new ResizeObserver(() => this.gridWidth.set(area.clientWidth));
      resizes.observe(area);
      this.destroyRef.onDestroy(() => resizes.disconnect());
    });
  }

  ngOnInit(): void {
    void this.views.load();
  }

  protected definitionOf(widget: DashboardWidgetInstance): DashboardWidgetDefinition | undefined {
    return this.registry.find(widget.type);
  }

  /** Starts a Customize draft and opens its panel; edits stay in the draft until Save or Cancel */
  protected customize(): void {
    if (!this.isMobile()) {
      this.session.begin();
    }
  }

  protected editSettings(widget: DashboardWidgetInstance): void {
    const definition = this.registry.find(widget.type);
    if (!definition) {
      return;
    }

    DashboardWidgetSettingsDialogComponent.open(this.dialog, { definition, instance: widget }, this.viewContainerRef)
      ?.afterClosed()
      .subscribe((settings) => {
        if (settings !== undefined) {
          this.session.apply({ kind: 'settings', id: widget.id, settings });
        }
      });
  }

  protected removeWidget(id: string): void {
    this.session.apply({ kind: 'remove', id });
    this.panel()?.focusClose();
  }

  protected arranged({ placements, keyboard }: DashboardGridArrangement): void {
    const frame = keyboard && this.frames().find((candidate) => candidate.instance().id === keyboard.id);
    // Reordering items in the DOM can drop focus; put it back on the handle being used
    const refocus = !!frame?.dragHandleHasFocus();

    this.session.apply({ kind: 'arrange', placements, keyboard });
    if (refocus) {
      frame?.focusDragHandle();
    }
  }

  protected moveWidget(id: string, delta: { dx: number; dy: number }): void {
    this.canvas()?.moveBy(id, delta);
  }

  protected resizeWidget(id: string, delta: { dw: number; dh: number }): void {
    this.canvas()?.resizeBy(id, delta);
  }
}

function sameRefreshSettings(
  a: DashboardRefreshSettings | undefined,
  b: DashboardRefreshSettings | undefined,
): boolean {
  return a?.enabled === b?.enabled && a?.intervalSeconds === b?.intervalSeconds;
}
